import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import BackButton from "../components/BackButton";
import BottomNav from "../components/BottomNav";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Handshake, MessageSquare, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const STATE_MAP = {
  pending: { label: "Active", tone: "border-nexus-blue/40 bg-nexus-blue/10 text-nexus-blue", desc: "Awaiting brand response" },
  accepted: { label: "Accepted", tone: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300", desc: "Turned into a collab" },
  rejected: { label: "Failed", tone: "border-nexus-pink/40 bg-nexus-pink/10 text-nexus-pink", desc: "Not selected" },
};

export default function Negotiations() {
  const { user, loading } = useAuth();
  const [apps, setApps] = useState([]);
  const [busy, setBusy] = useState(true);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    if (loading || !user) return;
    if (user.role !== "creator") return;
    api.get("/campaigns/mine/applications")
      .then((r) => setApps(r.data))
      .catch(() => toast.error("Failed to load negotiations"))
      .finally(() => setBusy(false));
  }, [user, loading]);

  const filtered = apps.filter((a) => (tab === "all" ? true : tab === "failed" ? a.status === "rejected" : a.status === "pending"));

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-6">
        <BackButton fallback="/creator" label="Back to dashboard" />
      </div>
      <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-4 pb-24 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
            <Handshake className="h-4 w-4" /> Negotiations
          </div>
          <h1 className="font-display font-bold text-4xl tracking-tighter">
            Every deal <span className="nexus-gradient-text">in motion.</span>
          </h1>
          <p className="text-zinc-400 mt-2 text-sm">Track your live pitches and see which ones didn't land — with the full briefing one tap away.</p>
        </div>

        <div className="flex gap-2" data-testid="negotiation-tabs">
          {[{ k: "all", l: "All" }, { k: "active", l: "Active" }, { k: "failed", l: "Failed" }].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`neg-tab-${t.k}`}
              className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${tab === t.k ? "nexus-gradient text-white border-transparent" : "bg-white/5 border-white/15 text-zinc-300 hover:bg-white/10"}`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {busy && <div className="text-zinc-500 text-sm animate-pulse">Loading…</div>}
        {!busy && filtered.length === 0 && (
          <div className="glass rounded-2xl p-10 text-center text-sm text-zinc-500">
            No {tab === "all" ? "" : tab + " "}negotiations yet. Apply to a Brand Collab to get started.
            <div className="mt-4"><Link to="/campaigns" className="text-nexus-blue hover:underline">Browse Brand Collabs →</Link></div>
          </div>
        )}
        <div className="space-y-3" data-testid="negotiations-list">
          {filtered.map((a) => {
            const s = STATE_MAP[a.status] || STATE_MAP.pending;
            const modeLabel = a.mode === "bid" ? "Your bid" : "Accepted rate";
            return (
              <div key={a.id} className="glass rounded-2xl p-5 flex items-center gap-4 flex-wrap card-hover" data-testid={`neg-item-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-bold text-lg truncate">{a.campaign?.title || "Untitled campaign"}</div>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${s.tone}`}>{s.label}</span>
                  </div>
                  <div className="text-xs text-zinc-400 mt-1">{a.campaign?.brand_name}</div>
                  <div className="text-xs text-zinc-500 mt-1">{s.desc}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{modeLabel}</div>
                  <div className="font-display font-bold text-2xl nexus-gradient-text tabular-nums">£{a.quoted_price}</div>
                </div>
                <div className="flex flex-col gap-2 basis-full sm:basis-auto">
                  {a.campaign_id && (
                    <Link
                      to={`/campaigns/${a.campaign_id}`}
                      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-nexus-blue hover:text-white"
                      data-testid={`neg-view-briefing-${a.id}`}
                    >
                      View briefing <ArrowRight className="h-3 w-3" />
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
